import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Activity, TrendingUp, Camera } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceArea } from "recharts";
import ExamScannerModal from "./ExamScannerModal";

const LEVEL_META = {
  ruim:       { label: "Ruim",      cls: "border-destructive/40 text-destructive bg-destructive/5" },
  regular:    { label: "Regular",   cls: "border-amber-500/40 text-amber-600 dark:text-amber-500 bg-amber-50 dark:bg-amber-900/10" },
  bom:        { label: "Bom",       cls: "border-emerald-400/40 text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/10" },
  otimo:      { label: "Ótimo",     cls: "border-success/40 text-success bg-success/10" },
  excelente:  { label: "Excelente", cls: "border-gold/60 text-gold bg-gold/10" },
  sem_referencia: { label: "S/ ref", cls: "border-muted-foreground/30 text-muted-foreground bg-muted" },
};

const LEVELS_ORDER = ["ruim", "regular", "bom", "otimo", "excelente"];

function fmtDate(iso) { try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; } }

function MarkerFormModal({ patientId, catalog, protocolsHistory, initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => initial ? {
    marker_id: initial.marker_id, value: initial.value,
    collection_date: (initial.collection_date || "").slice(0, 10),
    protocol_id: initial.protocol_id || "",
    manual_level: initial.manual_level || "",
  } : {
    marker_id: "", value: "", collection_date: new Date().toISOString().slice(0, 10),
    protocol_id: "", manual_level: "",
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  const save = async () => {
    if (!form.marker_id) { toast.error("Selecione um marcador."); return; }
    if (form.value === "" || Number.isNaN(Number(form.value))) { toast.error("Informe o valor numérico."); return; }
    if (!form.collection_date) { toast.error("Informe a data da coleta."); return; }
    setSaving(true);
    try {
      const payload = {
        marker_id: form.marker_id,
        value: Number(form.value),
        collection_date: form.collection_date,
        protocol_id: form.protocol_id || null,
        manual_level: form.manual_level || null,
      };
      if (isEdit) {
        await api.patch(`/admin/patients/${patientId}/markers/${initial.id}`, {
          ...payload,
          clear_manual: !form.manual_level && initial.is_manually_adjusted,
        });
        toast.success("Marcador atualizado.");
      } else {
        await api.post(`/admin/patients/${patientId}/markers`, payload);
        toast.success("Marcador registrado.");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar."); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose} data-testid="patient-marker-modal">
      <div className="bg-card border border-border rounded-sm p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline text-gold mb-1">{isEdit ? "Editar marcador" : "Novo marcador"}</div>
            <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">Registrar valor</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm" data-testid="pm-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="overline text-muted-foreground mb-1 block">Marcador</label>
            <select value={form.marker_id} onChange={(e) => setForm({ ...form, marker_id: e.target.value })}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="pm-marker-select">
              <option value="">Selecione…</option>
              {catalog.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.category}{m.unit ? ` · ${m.unit}` : ""})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline text-muted-foreground mb-1 block">Valor</label>
              <input type="number" step="0.001" value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                data-testid="pm-value-input" />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Data da coleta</label>
              <input type="date" value={form.collection_date}
                onChange={(e) => setForm({ ...form, collection_date: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                data-testid="pm-date-input" />
            </div>
          </div>
          {protocolsHistory.length > 0 && (
            <div>
              <label className="overline text-muted-foreground mb-1 block">Protocolo relacionado (opcional)</label>
              <select value={form.protocol_id} onChange={(e) => setForm({ ...form, protocol_id: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="pm-protocol-select">
                <option value="">— nenhum —</option>
                {protocolsHistory.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="overline text-muted-foreground mb-1 block">Sobrescrever nível (opcional)</label>
            <select value={form.manual_level} onChange={(e) => setForm({ ...form, manual_level: e.target.value })}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="pm-manual-level">
              <option value="">— calcular automaticamente —</option>
              {LEVELS_ORDER.map((l) => <option key={l} value={l}>{LEVEL_META[l].label}</option>)}
            </select>
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <button onClick={onClose} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted">Cancelar</button>
            <button onClick={save} disabled={saving} className="px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid="pm-save-btn">
              {saving ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PatientMarkersSection({ patientId, protocolsHistory = [] }) {
  const [records, setRecords] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [chartMarker, setChartMarker] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get(`/admin/patients/${patientId}/markers`),
        api.get("/admin/markers"),
      ]);
      setRecords(r.data || []);
      setCatalog(c.data || []);
      if (!chartMarker && r.data && r.data.length > 0) setChartMarker(r.data[0].marker_id);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar dados."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (rec) => {
    if (!window.confirm(`Excluir o registro de ${rec.marker_name} = ${rec.value}?`)) return;
    try {
      await api.delete(`/admin/patients/${patientId}/markers/${rec.id}`);
      toast.success("Registro excluído.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir."); }
  };

  const uniqueMarkers = useMemo(() => {
    const m = {};
    for (const r of records) if (r.marker_id && !m[r.marker_id]) m[r.marker_id] = { id: r.marker_id, name: r.marker_name };
    return Object.values(m);
  }, [records]);

  const chartData = useMemo(() => {
    if (!chartMarker) return { points: [], ranges: null };
    const rows = records
      .filter((r) => r.marker_id === chartMarker)
      .slice()
      .sort((a, b) => (a.collection_date || "").localeCompare(b.collection_date || ""));
    const ranges = rows[0]?.ranges || {};
    return {
      points: rows.map((r) => ({
        date: fmtDate(r.collection_date), value: r.value, level: r.effective_level,
      })),
      ranges,
      unit: rows[0]?.marker_unit || "",
      name: rows[0]?.marker_name || "",
    };
  }, [chartMarker, records]);

  return (
    <div className="space-y-4" data-testid="patient-markers-section">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="overline text-gold mb-1 inline-flex items-center gap-1"><Activity className="w-3 h-3" /> Marcadores clínicos</div>
          <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">Evolução de marcadores</h3>
          <p className="text-xs text-muted-foreground mt-1">Valores lançados classificados automaticamente pela Biblioteca de Marcadores (com sua personalização).</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setScanning(true)}
            className="inline-flex items-center gap-1 rounded-sm border border-gold bg-gold/10 text-primary px-3 py-2 text-xs font-semibold hover:bg-gold/20 transition-colors"
            data-testid="scanner-open-btn"
            title="Extrai marcadores automaticamente de uma foto ou PDF do exame com IA"
          >
            <Camera className="w-3.5 h-3.5" /> Scanner de Exame (IA)
          </button>
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1 rounded-sm bg-primary text-primary-foreground px-3 py-2 text-xs font-semibold hover:bg-primary/90 transition-colors"
            data-testid="add-patient-marker-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Adicionar marcador
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground overline">Carregando…</div>
      ) : records.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-8 text-center text-sm text-muted-foreground">
          Nenhum marcador registrado ainda. Clique em &quot;Adicionar marcador&quot; para começar.
        </div>
      ) : (
        <>
          {/* List */}
          <div className="bg-card border border-border rounded-sm divide-y divide-border">
            {records.map((r) => {
              const meta = LEVEL_META[r.effective_level] || LEVEL_META.sem_referencia;
              return (
                <div key={r.id} className="px-4 py-3 flex items-center gap-3 hover:bg-muted/40 transition-colors" data-testid={`pm-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-semibold text-sm">{r.marker_name}</span>
                      <span className="text-xs text-muted-foreground">= <span className="text-foreground font-semibold">{r.value}{r.marker_unit ? ` ${r.marker_unit}` : ""}</span></span>
                      <span className="text-xs text-muted-foreground">· {fmtDate(r.collection_date)}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <span className={`inline-block overline px-2 py-0.5 rounded-sm border ${meta.cls}`} data-testid={`pm-level-${r.id}`}>{meta.label}</span>
                      {r.range_label && (
                        <span
                          className="inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm border border-primary/40 bg-primary/5 text-primary"
                          data-testid={`pm-range-label-${r.id}`}
                        >
                          {r.range_label}
                        </span>
                      )}
                      {r.is_manually_adjusted && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground italic">
                          ajustado manualmente
                        </span>
                      )}
                      {r.protocol_id && protocolsHistory.find((p) => p.id === r.protocol_id) && (
                        <span className="text-[10px] text-muted-foreground">· {protocolsHistory.find((p) => p.id === r.protocol_id).name}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditing(r)} className="p-1.5 rounded-sm border border-border hover:bg-muted transition-colors" data-testid={`edit-pm-${r.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(r)} className="p-1.5 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5 transition-colors" data-testid={`delete-pm-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Chart */}
          {uniqueMarkers.length > 0 && (
            <div className="bg-card border border-border rounded-sm p-4" data-testid="pm-chart-block">
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="overline text-gold inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Evolução histórica</div>
                <select value={chartMarker} onChange={(e) => setChartMarker(e.target.value)}
                  className="px-3 py-1.5 rounded-sm border border-input bg-card text-xs"
                  data-testid="pm-chart-marker-select">
                  {uniqueMarkers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              {chartData.points.length < 2 ? (
                <div className="py-8 text-center text-xs text-muted-foreground">Registre ao menos 2 valores para ver a evolução.</div>
              ) : (
                <div style={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData.points} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                      <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      {chartData.ranges?.ideal_min != null && chartData.ranges?.ideal_max != null && (
                        <ReferenceArea y1={chartData.ranges.ideal_min} y2={chartData.ranges.ideal_max}
                          fill="hsl(var(--gold))" fillOpacity={0.12} stroke="hsl(var(--gold))" strokeOpacity={0.3} />
                      )}
                      <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => [`${v} ${chartData.unit}`, chartData.name]} />
                      <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              {chartData.ranges?.ideal_min != null && (
                <div className="text-[10px] text-muted-foreground mt-2 text-center">
                  Faixa dourada = zona ideal ({chartData.ranges.ideal_min}–{chartData.ranges.ideal_max} {chartData.unit})
                </div>
              )}
            </div>
          )}
        </>
      )}

      {(editing || creating) && (
        <MarkerFormModal
          patientId={patientId}
          catalog={catalog}
          protocolsHistory={protocolsHistory}
          initial={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={async () => { setEditing(null); setCreating(false); await load(); }}
        />
      )}

      {scanning && (
        <ExamScannerModal
          patientId={patientId}
          catalog={catalog}
          onClose={() => setScanning(false)}
          onSaved={async () => { await load(); }}
        />
      )}
    </div>
  );
}
